import { useCallback, useEffect, useState } from "react";
import {
  createSavedView,
  deleteSavedView,
  fetchSavedViews,
  updateSavedView,
} from "../api/savedViews";
import type { PersistedFilterState } from "../utils/filterPersistence";
import type { SavedView } from "../utils/savedViews";
import { readSavedViews, writeSavedViews } from "../utils/savedViews";
import { getImpersonationDomain } from "../../impersonation/storage";

/**
 * The saved-view list, owned by global-api with localStorage as a cache.
 *
 * The cache is what makes the strip paint immediately instead of flashing
 * empty on every visit, and what keeps it usable when the request fails — a
 * network blip shouldn't look like your views were deleted. The server is the
 * source of truth: a successful load replaces the cache wholesale.
 *
 * Writes are refused server-side while impersonating (the read-only rule in
 * `_apply_impersonation`, mirrored in global-api's `get_caller`), so
 * `readOnly` hides the controls rather than letting a request fail — the same
 * shape as the "(read-only)" banner the app already shows in that state.
 *
 * Every write only touches the local list once the server has agreed, which
 * means a rejected one leaves the strip *correct* but silent — a rename that
 * didn't happen looks exactly like a rename that did. So each returns whether
 * it landed and reports what failed in `writeError`, rather than rejecting
 * into a caller that has already closed its popover.
 */
export function useSavedViews(namespace: string) {
  const [views, setViews] = useState<SavedView[]>(
    () => readSavedViews(namespace).views,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [writeError, setWriteError] = useState<string | null>(null);

  const adopt = useCallback(
    (next: SavedView[]) => {
      setViews(next);
      writeSavedViews(namespace, next);
    },
    [namespace],
  );

  /**
   * Run a write, reporting a failure instead of dropping it. `failure` is what
   * the strip says when it doesn't land; the boolean is what lets a caller keep
   * its popover open, with what you typed still in it, so retrying is one click.
   */
  const attempt = useCallback(
    async (failure: string, write: () => Promise<void>): Promise<boolean> => {
      setWriteError(null);
      try {
        await write();
        return true;
      } catch {
        setWriteError(failure);
        return false;
      }
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchSavedViews(namespace)
      .then((remote) => {
        if (cancelled) return;
        adopt(remote);
        setError(false);
      })
      .catch(() => {
        // Keep whatever the cache holds; an empty strip would read as data loss.
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [namespace, adopt]);

  const create = useCallback(
    (name: string, slice: PersistedFilterState) =>
      attempt(`Couldn't save "${name}" — it wasn't added.`, async () => {
        const created = await createSavedView(namespace, { name, slice });
        setViews((prev) => {
          const next = [...prev, created];
          writeSavedViews(namespace, next);
          return next;
        });
      }),
    [namespace, attempt],
  );

  const update = useCallback(
    (id: string, name: string, slice: PersistedFilterState) =>
      attempt(`Couldn't save your changes to "${name}" — it's unchanged.`, async () => {
        const updated = await updateSavedView(namespace, id, { name, slice });
        setViews((prev) => {
          // Replace in place: an edit keeps the view's id and its position in
          // the strip, which is the whole point of editing rather than
          // re-saving.
          const next = prev.map((v) => (v.id === id ? updated : v));
          writeSavedViews(namespace, next);
          return next;
        });
      }),
    [namespace, attempt],
  );

  const remove = useCallback(
    (id: string) => {
      const name = views.find((v) => v.id === id)?.name;
      return attempt(
        `Couldn't delete ${name ? `"${name}"` : "that view"} — it's still here.`,
        async () => {
          await deleteSavedView(id);
          setViews((prev) => {
            const next = prev.filter((v) => v.id !== id);
            writeSavedViews(namespace, next);
            return next;
          });
        },
      );
    },
    [namespace, views, attempt],
  );

  return {
    views,
    loading,
    /** True when the last load failed and the strip is showing cached views. */
    error,
    /** What the last write failed to do, until the next one is attempted. */
    writeError,
    /** Writes are rejected server-side while viewing as a customer. */
    readOnly: !!getImpersonationDomain(),
    create,
    update,
    remove,
  };
}
